import {
  getAcademicRiskStudentUseCase,
  getAllStudentUseCase,
  getCareerLayerUseCase,
  getCenterLayerUseCase,
  getCharacterizationUseCase,
  getMetricsForTheHomeUseCase,
  getUsersUseCase,
} from '@/di';
import { PathRoute } from '@/domains/routes/route.entity';
import type { Center } from '@/domains/shared';
import { lazy } from 'react';
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';
import { privateRouteGuard } from '../guards/privateRouteLoaderGuard';

/**
 * Extensión de RouteObject de React Router con tipos personalizados
 * Permite usar PathRoute (enum) en lugar de strings para mayor seguridad de tipos
 *
 * @interface CustomRouteObject
 * @extends {Omit<RouteObject, 'path'>}
 * @property {PathRoute | string} [path] - Ruta personalizada que acepta PathRoute o string
 */
export interface CustomRouteObject extends Omit<RouteObject, 'path'> {
  path?: PathRoute | string;
}

// Pages cargadas con lazy loading
const Home = lazy(() => import('@/pages/private/HomePage'));
const Student = lazy(() => import('@/pages/private/StudentPage'));
const CenterLayer = lazy(() => import('@/pages/private/CenterLayerPage'));
const CareerLayer = lazy(() => import('@/pages/private/CareerLayerPage'));
const Characterization = lazy(
  () => import('@/pages/private/CharacterizationPage'),
);
const AcademicRisk = lazy(() => import('@/pages/private/AcademicRiskPage'));
const User = lazy(() => import('@/pages/private/UserPage'));

/**
 * Configuración de rutas privadas de la aplicación
 * Todas las rutas están protegidas con privateRouteGuard excepto Characterization
 * Cada ruta carga datos mediante use cases antes del renderizado (loader pattern)
 *
 * Rutas incluidas:
 * - /Home (index): Dashboard principal con métricas
 * - /center/:center (CenterLayer): Vista por centro de estudio
 * - /career/:career (CareerLayer): Vista por carrera
 * - /estudiante (Student): Listado de estudiantes
 * - /caracterizacion/:cod (Characterization): Perfil de estudiante
 * - /riesgo-academico (AcademicRisk): Vista de riesgo académico
 * - /usuario (User): Perfil de usuario
 *
 * @constant {CustomRouteObject[]} privateRoutes - Array de configuración de rutas privadas
 */
export const privateRoutes: CustomRouteObject[] = [
  {
    index: true,
    element: <Home />,
    loader: privateRouteGuard(() => getMetricsForTheHomeUseCase().execute()),
  },
  {
    path: PathRoute.LAYER_CENTER,
    element: <CenterLayer />,
    loader: privateRouteGuard(async ({ params }: LoaderFunctionArgs) => {
      const center = params?.center?.toUpperCase() as Center;
      if (!center) throw new Error('Centro de estudio requerido');

      return await getCenterLayerUseCase().execute(center);
    }),
  },
  {
    path: PathRoute.LAYER_CAREER,
    element: <CareerLayer />,
    loader: privateRouteGuard(async ({ params }: LoaderFunctionArgs) => {
      const career = params?.career?.toUpperCase();
      if (!career) throw new Error('Carrera requerida');

      return await getCareerLayerUseCase().execute(career);
    }),
  },
  {
    path: PathRoute.ESTUDIANTE,
    element: <Student />,
    loader: privateRouteGuard(() => getAllStudentUseCase().execute()),
  },
  {
    path: PathRoute.CHARACTERIZATION,
    element: <Characterization />,
    loader: privateRouteGuard(async ({ params }: LoaderFunctionArgs) => {
      const codIncorporacionEstudiante = params?.cod_incorporacion_estudiante;
      if (!codIncorporacionEstudiante) {
        throw new Error('Código de incorporación del estudiante requerido');
      }

      return await getCharacterizationUseCase().execute(
        codIncorporacionEstudiante,
      );
    }),
  },
  {
    path: PathRoute.RIESGO_ACADEMICO,
    element: <AcademicRisk />,
    loader: privateRouteGuard(() => getAcademicRiskStudentUseCase().execute()),
  },
  {
    path: PathRoute.USUARIO,
    element: <User />,
    loader: privateRouteGuard(() => getUsersUseCase().execute()),
  },
];
